import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useCompanyStore, getAuthAxios } from '@/store';
import { useSidebarContext } from './SidebarContext';
import { Menu, User, Settings, LogOut, Bell, Megaphone, X, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/components/ThemeProvider';
import { useI18n } from '@/components/I18nProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

export const Topbar = ({ title }) => {
  const { user, logout } = useAuthStore();
  const { company } = useCompanyStore();
  const { setSidebarOpen } = useSidebarContext();
  const { theme, setTheme } = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnns, setDismissedAnns] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_announcements') || '[]'); } catch { return []; }
  });
  // Profil düzenleme modalı
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileNewPass, setProfileNewPass] = useState('');
  const [profileCurrentPass, setProfileCurrentPass] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const openProfile = () => {
    setProfileName(user?.name || '');
    setProfileNewPass('');
    setProfileCurrentPass('');
    setProfileOpen(true);
  };

  const saveProfile = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = useAuthStore.getState().token;
    setProfileSaving(true);
    try {
      let nameChanged = false;
      let passChanged = false;
      if (profileName.trim() && profileName.trim() !== user?.name) {
        await axios.put(`${API_URL}/api/auth/profile`, { name: profileName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
        nameChanged = true;
      }
      if (profileNewPass) {
        if (!profileCurrentPass) { toast.error('Mevcut şifrenizi girin'); setProfileSaving(false); return; }
        await axios.put(`${API_URL}/api/auth/change-password`, { current_password: profileCurrentPass, new_password: profileNewPass }, { headers: { Authorization: `Bearer ${token}` } });
        passChanged = true;
      }
      if (!nameChanged && !passChanged) {
        toast.info('Değişiklik yok');
      } else {
        // Store'daki user'ı güncelle
        if (nameChanged) {
          useAuthStore.setState({ user: { ...user, name: profileName.trim() } });
        }
        toast.success(passChanged ? 'Profil ve şifre güncellendi' : 'Profil güncellendi');
        setProfileOpen(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setProfileSaving(false);
    }
  };

  const fetchUnread = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch {}
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/announcements');
      setAnnouncements(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUnread();
    fetchAnnouncements();
    const interval = setInterval(() => { fetchUnread(); fetchAnnouncements(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchUnread, fetchAnnouncements]);

  const handleOpenNotif = () => {
    setNotifOpen(true);
    fetchNotifications();
  };

  const handleMarkAllRead = async () => {
    try {
      const api = getAuthAxios();
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch {}
  };

  const dismissAnnouncement = (id) => {
    const updated = [...dismissedAnns, id];
    setDismissedAnns(updated);
    localStorage.setItem('dismissed_announcements', JSON.stringify(updated));
  };

  const getInitials = (name) => {
    return name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      const date = new Date(d);
      return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const typeColors = {
    welcome: 'bg-green-100 text-green-700',
    subscription: 'bg-blue-100 text-blue-700',
    trial_expired: 'bg-red-100 text-red-700',
    whatsapp_disconnect: 'bg-orange-100 text-orange-700',
    announcement: 'bg-purple-100 text-purple-700',
  };

  const typeLabels = {
    welcome: 'Hosgeldin',
    subscription: 'Abonelik',
    trial_expired: 'Sure Doldu',
    whatsapp_disconnect: 'WhatsApp',
    announcement: 'Duyuru',
  };

  const annTypeColors = {
    info: 'border-blue-300 bg-blue-50 text-blue-900',
    warning: 'border-amber-300 bg-amber-50 text-amber-900',
    success: 'border-green-300 bg-green-50 text-green-900',
    error: 'border-red-300 bg-red-50 text-red-900',
  };

  const visibleAnns = announcements.filter(a => !dismissedAnns.includes(a.id));

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/95 px-3 backdrop-blur sm:h-16 sm:px-6 supports-[backdrop-filter]:bg-background/60" data-testid="topbar">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="hamburger-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <h1 className="font-heading text-lg font-semibold sm:text-xl">{user?.role === 'super_admin' ? 'Yönetim Paneli' : (company?.name || title)}</h1>
        </div>

        <div className="flex items-center gap-1">
          {/* Notifications Bell */}
          <Popover open={notifOpen} onOpenChange={setNotifOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" onClick={handleOpenNotif} data-testid="notifications-btn">
                <Bell className="h-5 w-5" />
                {(unreadCount + visibleAnns.length) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                    {unreadCount + visibleAnns.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end" data-testid="notifications-popover">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <h3 className="font-medium text-sm">Bildirimler</h3>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-auto py-1 px-2 text-xs" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                    Tumunu oku
                  </Button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {/* Announcements section */}
                {visibleAnns.length > 0 && (
                  <div className="border-b">
                    {visibleAnns.map((a) => (
                      <div
                        key={a.id}
                        className="border-b last:border-0 px-4 py-3 bg-primary/5"
                        data-testid={`ann-notif-${a.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <Megaphone className="h-3.5 w-3.5 text-primary shrink-0" />
                              <span className="font-medium text-sm truncate">{a.title}</span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{a.content}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => dismissAnnouncement(a.id)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-100 text-purple-700">
                            Duyuru
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">{formatDate(a.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Regular notifications */}
                {notifications.length === 0 && visibleAnns.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">Bildirim yok</div>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <div
                      key={n.id}
                      className={`border-b last:border-0 px-4 py-3 transition-colors ${!n.is_read ? 'bg-primary/5' : ''}`}
                      data-testid={`notif-${n.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{n.title}</span>
                            {!n.is_read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{n.message?.split('\n')[0]}</p>
                        </div>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${typeColors[n.type] || ''}`}>
                          {typeLabels[n.type] || n.type}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">{formatDate(n.created_at)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full sm:h-10 sm:w-10" data-testid="user-menu-btn">
                <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs sm:text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openProfile} data-testid="menu-profile">
                <User className="mr-2 h-4 w-4" />Profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/admin/settings')} data-testid="menu-settings">
                <Settings className="mr-2 h-4 w-4" />Ayarlar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Announcement Banners */}
      {visibleAnns.length > 0 && (
        <div className="sticky top-14 sm:top-16 z-20 space-y-0" data-testid="announcement-banners">
          {visibleAnns.slice(0, 3).map((a) => (
            <div
              key={a.id}
              className={`flex items-center justify-between px-4 py-2 border-b text-sm ${annTypeColors[a.type] || annTypeColors.info}`}
              data-testid={`ann-banner-${a.id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Megaphone className="h-4 w-4 shrink-0" />
                <span className="font-medium truncate">{a.title}</span>
                <span className="hidden sm:inline text-xs opacity-80 truncate">— {a.content}</span>
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 hover:bg-black/10" onClick={() => dismissAnnouncement(a.id)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Profil düzenleme modalı */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="sm:max-w-md" data-testid="profile-edit-dialog">
          <DialogHeader>
            <DialogTitle>Profil Bilgileri</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="prof-name">Ad Soyad</Label>
              <Input
                id="prof-name"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="Adınız Soyadınız"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>E-posta</Label>
              <Input value={user?.email || ''} disabled className="bg-gray-50" />
            </div>
            <div className="border-t pt-4 mt-2">
              <p className="text-xs font-medium text-gray-500 uppercase mb-3">Şifre Değiştir (opsiyonel)</p>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="prof-cur">Mevcut Şifre</Label>
                  <Input
                    id="prof-cur"
                    type="password"
                    value={profileCurrentPass}
                    onChange={(e) => setProfileCurrentPass(e.target.value)}
                    placeholder="Mevcut şifreniz"
                    data-testid="profile-current-pass-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="prof-new">Yeni Şifre</Label>
                  <Input
                    id="prof-new"
                    type="password"
                    value={profileNewPass}
                    onChange={(e) => setProfileNewPass(e.target.value)}
                    placeholder="Boş bırakılırsa değişmez"
                    data-testid="profile-new-pass-input"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileOpen(false)} disabled={profileSaving} data-testid="profile-cancel-btn">İptal</Button>
            <Button onClick={saveProfile} disabled={profileSaving} data-testid="profile-save-btn">
              {profileSaving ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
